import React, { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useEstablishmentPermissions } from '../../hooks/useEstablishmentPermissions';
import { LoadingSpinner } from '../ui/LoadingSpinner';

interface RequirePermissionProps {
  permission?: string;
  permissions?: string[];
  requireAll?: boolean;
  children: ReactNode;
  fallback?: ReactNode;
  redirectTo?: string;
}

/**
 * Route guard component that checks if user has required permissions
 * @param permission - Single permission to check
 * @param permissions - Array of permissions to check
 * @param requireAll - If true, requires all permissions. If false, requires at least one
 * @param children - Content to render if permission check passes
 * @param fallback - Content to render if permission check fails (default: redirect)
 * @param redirectTo - Route to redirect to if permission check fails (default: /403)
 */
export const RequirePermission: React.FC<RequirePermissionProps> = ({
  permission,
  permissions,
  requireAll = false,
  children,
  fallback,
  redirectTo = '/403'
}) => {
  const location = useLocation();
  const { hasPermission, hasAnyPermission, hasAllPermissions, isLoading } = useEstablishmentPermissions();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  let hasRequiredPermission = false;

  if (permission) {
    hasRequiredPermission = hasPermission(permission);
  } else if (permissions && permissions.length > 0) {
    hasRequiredPermission = requireAll 
      ? hasAllPermissions(permissions)
      : hasAnyPermission(permissions);
  } else {
    // No permissions specified, allow access
    hasRequiredPermission = true;
  }

  if (!hasRequiredPermission) {
    if (fallback) {
      return <>{fallback}</>;
    }
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  return <>{children}</>;
};